import { useCallback, useEffect, useId, useRef, useState } from "react";
import { F, type Row } from "./types";
import { AUDIO_H, formatDuration, fullSrc, parsePeaks } from "./rows";
import { claim, release } from "./player";

const W = 128;
const H = 32;
const FLAT = Array.from({ length: 64 }, () => 0.12);

/** 64 bars as ONE <path>, not 64 elements: at twenty visible rows that would be
 *  1,280 nodes for the browser to lay out on every scroll frame. */
function barsPath(peaks: number[]): string {
  const step = W / peaks.length;
  let d = "";
  for (let i = 0; i < peaks.length; i++) {
    const x = (i * step + step / 2).toFixed(2);
    const h = Math.max(1, Math.min(1, Math.abs(peaks[i])) * H);
    d += `M${x} ${((H - h) / 2).toFixed(2)}v${h.toFixed(2)}`;
  }
  return d;
}

/** A voice note: play, the waveform, the length. Fixed height whatever the
 *  peaks say, so a note can never resize the list. Nothing is fetched until you
 *  tap -- the <audio> element does not exist before then.
 *
 *  Each row still owns its own <audio>; what P6 adds is that they all defer to
 *  one claim in player.ts, so starting a second note stops the first. The claim
 *  is taken from onPlay rather than from the tap handler because that is the
 *  event that fires whichever way playback started -- autoPlay on mount, the
 *  button, or the native controls.
 */
export function Waveform({ row }: { row: Row }) {
  const peaks = parsePeaks(row) ?? FLAT;   // a corrupt row costs its waveform, not its player
  const d = barsPath(peaks);
  const clip = useId().replace(/:/g, "");  // useId's colons are invalid in a url(#...)
  const audio = useRef<HTMLAudioElement>(null);
  const raf = useRef<number | null>(null);
  const [mounted, setMounted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [progress, setProgress] = useState(0);

  // Only while playing. A rAF loop that keeps running behind a paused note is
  // a wakeup per frame for every note you have ever opened.
  const tick = useCallback(() => {
    const el = audio.current;
    if (el && el.duration > 0) setProgress(el.currentTime / el.duration);
    raf.current = requestAnimationFrame(tick);
  }, []);

  // A virtualized row that scrolls out of the window takes its <audio> with
  // it. Without this the registry keeps pointing at a detached element and the
  // next note has nothing real to stop.
  useEffect(() => () => release(audio.current), []);

  useEffect(() => {
    if (playing) raf.current = requestAnimationFrame(tick);
    return () => { if (raf.current !== null) cancelAnimationFrame(raf.current); raf.current = null; };
  }, [playing, tick]);

  function toggle() {
    if (!mounted) { setMounted(true); return; }   // autoPlay starts it
    const el = audio.current;
    if (!el) return;
    if (el.paused) void el.play();
    else el.pause();
  }

  return (
    <div className="audio" style={{ height: AUDIO_H }}>
      <button type="button" className="tappable" onClick={toggle}
              aria-label={playing ? "pause voice note" : "play voice note"}>
        {playing ? "❚❚" : "▶"}
      </button>
      <svg viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" aria-hidden="true">
        <defs>
          <clipPath id={clip}>
            <rect x="0" y="0" width={(W * progress).toFixed(2)} height={H} />
          </clipPath>
        </defs>
        <path className="wave" d={d} />
        <path className="wave played" d={d} clipPath={`url(#${clip})`} />
      </svg>
      <span className="dur">{formatDuration(row[F.DurationS])}</span>
      {mounted && (
        <audio
          ref={audio}
          src={fullSrc(row)!}
          autoPlay
          preload="none"
          onPlay={(e) => { claim(e.currentTarget); setPlaying(true); }}
          onPause={(e) => { release(e.currentTarget); setPlaying(false); }}
          onEnded={(e) => { release(e.currentTarget); setPlaying(false); setProgress(0); }}
        />
      )}
    </div>
  );
}
